import * as React from 'react'
import { Plus } from '@phosphor-icons/react'
import { SettingsBreadcrumbs } from 'components/settings-breadcrumbs'
import { SettingsTitle } from 'components/settings-title'
import { useApiClient } from '../../utils/use-api-client'
import { successToast } from '../../utils/success-toast'
import { errorToast } from '../../utils/error-toast'
import { CycleListItem } from './cycle-list-item'
import { NewCycleModal } from './new-cycle-modal'
import { RemoveCycleDialog } from './remove-cycle-dialog'
import { ScopeSelector, CycleScope } from './scope-selector'
import {
  CheckinCycle,
  CheckinCyclesIndexResponse,
  CheckinCyclesPageProps,
} from './types'

export const CheckinCyclesPage: React.FC<CheckinCyclesPageProps> = ({
  orgSlug,
  organisationString,
}) => {
  const apiClient = useApiClient()

  const [cycles, setCycles] = React.useState<CheckinCycle[]>([])
  const [scope, setScope] = React.useState<CycleScope>('active')
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editingCycle, setEditingCycle] = React.useState<CheckinCycle | null>(
    null,
  )
  // A cycle being reused as a template: the modal prefills from it but saves a
  // brand-new cycle (the source archived cycle is left unchanged).
  const [templateCycle, setTemplateCycle] = React.useState<CheckinCycle | null>(
    null,
  )
  const [removingCycle, setRemovingCycle] = React.useState<CheckinCycle | null>(
    null,
  )
  const [isRemoving, setIsRemoving] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setLoadError(null)

      try {
        const response = await apiClient.get<CheckinCyclesIndexResponse>(
          `/api/v1/internal/orgs/${orgSlug}/checkin_cycles`,
        )
        if (cancelled) return

        setCycles(
          (response?.data ?? []).map((resource) => ({
            id: resource.id,
            ...resource.attributes,
          })),
        )
      } catch (err: unknown) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Failed to load check-in cycles.'
        setLoadError(message)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [apiClient, orgSlug])

  const activeCycles = React.useMemo(
    () => cycles.filter((cycle) => !cycle.archived),
    [cycles],
  )
  const archivedCycles = React.useMemo(
    () => cycles.filter((cycle) => cycle.archived),
    [cycles],
  )

  const visibleCycles = scope === 'active' ? activeCycles : archivedCycles

  // Cycle names are unique among ACTIVE cycles only, so the archived list is
  // excluded — that's what lets an archived cycle keep the name it was created
  // with. The cycle being edited is excluded too, so saving it unchanged is not
  // a clash with itself.
  const takenNames = React.useMemo(
    () =>
      activeCycles
        .filter((cycle) => cycle.id !== editingCycle?.id)
        .map((cycle) => cycle.name),
    [activeCycles, editingCycle],
  )

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCycle(null)
    setTemplateCycle(null)
  }

  const handleConfirmRemove = async () => {
    if (!removingCycle || isRemoving) return
    setIsRemoving(true)

    try {
      await apiClient.delete(
        `/api/v1/internal/orgs/${orgSlug}/checkin_cycles/${removingCycle.slug}`,
      )
      setCycles((current) =>
        current.filter((cycle) => cycle.id !== removingCycle.id),
      )
      successToast('Cycle removed successfully')
      setRemovingCycle(null)
    } catch (err: unknown) {
      errorToast(err instanceof Error ? err.message : undefined)
    } finally {
      setIsRemoving(false)
    }
  }

  let cycleListContent: React.ReactNode
  if (isLoading) {
    cycleListContent = (
      <p className="text-base text-gray-500">Loading check-in cycles…</p>
    )
  } else if (loadError) {
    cycleListContent = <p className="text-base text-red-600">{loadError}</p>
  } else if (visibleCycles.length === 0) {
    cycleListContent = (
      <p className="text-base text-gray-500">
        {scope === 'active'
          ? 'No active check-in cycles yet.'
          : 'No archived check-in cycles.'}
      </p>
    )
  } else {
    cycleListContent = (
      <ul className="flex list-none flex-col gap-4 pl-0">
        {visibleCycles.map((cycle) => (
          <CycleListItem
            key={cycle.id}
            cycle={cycle}
            onEdit={setEditingCycle}
            onUseAsTemplate={setTemplateCycle}
            onRemove={setRemovingCycle}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className="pb-16">
      <SettingsBreadcrumbs
        breadcrumbs={[organisationString, 'Check-ins', 'Check-in cycles']}
      />
      <SettingsTitle>Check-in cycles</SettingsTitle>

      <div className="mb-6 flex items-center justify-between gap-4">
        <ScopeSelector
          value={scope}
          activeCount={activeCycles.length}
          archivedCount={archivedCycles.length}
          onChange={setScope}
        />

        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex h-[36px] items-center justify-center gap-2 rounded-lg bg-[#2E9E5D] py-2 pl-2 pr-3 text-center font-talent text-[16px] font-medium leading-[20px] text-white hover:bg-[#268650]"
        >
          <Plus size={16} weight="bold" />
          Add new cycle
        </button>
      </div>

      {cycleListContent}

      {(isModalOpen || editingCycle || templateCycle) && (
        <NewCycleModal
          key={
            editingCycle?.id ??
            (templateCycle ? `template-${templateCycle.id}` : 'new')
          }
          orgSlug={orgSlug}
          cycle={editingCycle ?? templateCycle ?? undefined}
          asTemplate={Boolean(templateCycle)}
          takenNames={takenNames}
          onClose={closeModal}
          onCreated={(cycle) => {
            setCycles((current) => [cycle, ...current])
            setScope('active')
          }}
          onUpdated={(updated) => {
            setCycles((current) =>
              current.map((cycle) =>
                cycle.id === updated.id ? updated : cycle,
              ),
            )
          }}
        />
      )}

      {removingCycle && (
        <RemoveCycleDialog
          removing={isRemoving}
          onCancel={() => {
            if (!isRemoving) setRemovingCycle(null)
          }}
          onConfirm={handleConfirmRemove}
        />
      )}
    </div>
  )
}
